import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Me } from '../types';
import { useStore } from '../store';
import { mayOpen } from './homePage';
import { pageHref } from './newTab';

// ── Every app-wide pop-up has a home page, and is asked in ONE browser tab (report c2846304) ─────────
// "When AI brings up screen pop-ups, make them appear only on the relevant menu page. For example, Job
// Related should appear on the Active Job page. Filing Related should appear on the Filing page, etc. If
// the Filing page is not already open or the Active Job page is not already open, open the Active Job page
// tab automatically and pop up on that tab/page ONLY. Do not pop up on every tab/page as this is not
// productive the user does not know whether they have dealt with the pop-up already and gets confused."
//
// Until now each of these dialogs was mounted app-wide (App.tsx, AuthGate) and drew itself wherever it
// found itself: with six SLF tabs open, the same question was asked six times over, and answering it in
// one tab left the other five still asking until their next poll. Each pop-up now names the page it
// belongs on and asks this service whether THIS tab is the one to ask in:
//
//   usePopupHome(<page key>, <there is something to ask>) → 'show' | 'notice' | 'hidden'
//
//   'show'   — draw the dialog here: this tab is showing that page (or one of the three fallbacks below).
//   'notice' — this is the tab in front and no tab has the page open: draw ONE small card instead
//              (components/PopupHomeNotice) that opens the page on a click. A window.open with no click
//              behind it is blocked by every browser, so the card is how the page gets opened.
//   'hidden' — another tab has it. Draw nothing.
//
// The dialog is never drawn in two tabs at once, and nothing is dismissed or answered on anyone's behalf:
// a question skipped here is simply asked in the tab that owns its page.

export type PopupHome = 'show' | 'notice' | 'hidden';

// ── Home page per pop-up ─────────────────────────────────────────────────────────────────────────────
// The job questions (JobStatusConfirm, CloseJobWatcher, JobCommentPing, TrPassportPrompt) are all
// answered on Active Jobs; Our Documents' name questions and Drop Folder's proposals (NameChecks,
// DropProposals) on Filing.
export const HOME_JOBS = 'active_jobs';
export const HOME_FILING = 'doc_storage';

/** My Tasks' urgent alert: a job among its rows belongs on Active Jobs, and a money row on the page that
 *  row came from — the bank statement of its own account, or Petty Cash (routes/myTasks.ts `source`). */
export const tasksAlertHome = (alerts: Array<{ kind?: string; source?: string }>): string =>
  (alerts.some(a => a.kind === 'job') ? HOME_JOBS : String(alerts[0]?.source || ''));

/** The client-payment-slip pop-up: the page its own one button opens — the bank page of the account a
 *  lease-registration slip was paid into, the quotation otherwise (components/SlipAlert). */
export const slipAlertHome = (slip: { source?: string; scope?: string } | undefined): string =>
  (!slip ? '' : slip.source === 'lease_reg' ? (slip.scope === 'client' ? 'bank_client' : 'bank_legal') : 'quotation');

/** Holds the home page of a pop-up that works it out from its own rows — My Tasks' urgent alert and the
 *  client-payment-slip pop-up above — still while the pop-up is up. Both read it off the FIRST of the
 *  rows the 5-minute poll returned, so a poll that came back in another order, or carrying a row the
 *  server named no page for (a blank key, which means "ask wherever the user is"), moved the same
 *  question between the dialog and the card on its own (report c2846304, opening #2). The page is settled
 *  once, when the question appears, and worked out afresh only once that question is gone. */
export function useHomeKey(key: string, pending: boolean): string {
  const held = useRef('');
  const settled = useRef(false);
  if (!pending) { settled.current = false; return key; }
  if (!settled.current) { settled.current = true; held.current = key; }
  return held.current;
}

// Every page any of them calls home. Only these take a window name (below): a tab showing any other page
// is left exactly as it is.
const HOME_PAGES = new Set([HOME_JOBS, HOME_FILING, 'bank_acc', 'bank_legal', 'bank_client', 'pettycash', 'quotation']);

// What a page is called on the card, in the words its own top-menu button uses: Filing reads "Filing"
// (tab_upload_doc), not the module name, as the browser tab does (App.tsx DocumentTitle).
const PAGE_LABEL: Record<string, string> = { doc_storage: 'tab_upload_doc', taken_return: 'tab_taken_return' };
export const pageLabelKey = (key: string) => PAGE_LABEL[key] || `mod_${key}`;

// ── Which page this tab is showing ───────────────────────────────────────────────────────────────────
// A module page says so in its own address, /m/<key>. The Dashboard and a My Dashboard(s) menu are one
// address whatever page is open on them, so they say which one themselves — and it is the page actually
// DRAWN, not the ?tab= the address asked for: the Dashboard falls back to Active Jobs, or to the first
// tab this login may open, whenever the requested one isn't theirs (Portal: effKey).
let dashPage = '';
const dashSubs = new Set<() => void>();

/** For the Dashboard (Portal: DashPageMark, off effKey) and Quick Launch (FocusDashboard, off the open
 *  page): while this is mounted, that is the page this tab shows. */
export function useDashPage(key: string): void {
  useEffect(() => {
    dashPage = key;
    dashSubs.forEach(f => f());
    return () => {
      if (dashPage !== key) return;   // another page has already taken over
      dashPage = '';
      dashSubs.forEach(f => f());
    };
  }, [key]);
}

const pageOfPath = (path: string): string =>
  (path.indexOf('/m/') === 0 ? (path.split('/')[2] || '') : path === '/' || path === '/focus' ? dashPage : '');

/** Read fresh, so the heartbeat and each look are right whether or not React has redrawn since. */
const pageHere = (): string => pageOfPath(window.location.pathname);

// ── The browser's tabs, as they tell each other where they are ───────────────────────────────────────
// Each tab writes {key, at, vis, ask} into one shared record every BEAT_MS: the page it is showing, when
// it last spoke, when it was last looked at, and which pages it is asking a question on (the claim
// below). A tab that has not spoken for GONE_MS is gone (closed, or asleep) and its page is up for grabs
// again; a question it was asking is held for it a while longer (CLAIM_MS — see the claim).
type Beat = { key: string; at: number; vis: number; ask?: Record<string, number> };
type Beats = Record<string, Beat>;
const TAB_KEY = 'mf_popup_tab';                                   // sessionStorage: this tab's own name
const beatsKey = (userId: string) => `mf_popupTabs:${userId}`;    // localStorage: all of them
const sentKey = (userId: string) => `mf_popupSent:${userId}`;     // localStorage: pages a tab was sent to
const BEAT_MS = 4_000;
const GONE_MS = 13_000;        // three beats and a little
const GRACE_MS = 6_000;        // a skipped beat is not a tab letting go of its question
const CLAIM_MS = 75_000;       // a question held by a tab the browser has sent to sleep (see below)
const SENT_MS = 20_000;        // a tab was sent to a page and the page never came up
const LOOK_MS = 2_000;         // how often a pop-up weighs the answer again

let tabId = '';
/** This tab's name, kept in sessionStorage so a refresh is the same tab and a new tab is a new one. */
function myTab(): string {
  if (tabId) return tabId;
  try {
    const kept = sessionStorage.getItem(TAB_KEY);
    if (kept) { tabId = kept; return tabId; }
  } catch { /* no session storage — a name for this page's life does just as well */ }
  tabId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
  try { sessionStorage.setItem(TAB_KEY, tabId); } catch { /* as above */ }
  return tabId;
}

/** null = this browser will not let us share anything (site data blocked, private mode, quota) — the
 *  first of the three fallbacks: such a tab asks its questions itself, exactly as every tab used to. */
function readBeats(userId: string): Beats | null {
  let raw: string | null = null;
  try { raw = localStorage.getItem(beatsKey(userId)); } catch { return null; }
  let v: any = null;
  try { v = JSON.parse(raw || '{}'); } catch { v = null; }   // garbage: the next beat writes it afresh
  const out: Beats = {};
  if (v && typeof v === 'object' && !Array.isArray(v)) {
    for (const [id, b] of Object.entries<any>(v)) {
      if (!b || !Number.isFinite(b.at) || !Number.isFinite(b.vis)) continue;
      const one: Beat = { key: String(b.key || ''), at: b.at, vis: b.vis };
      // The pages that tab says it is asking on, kept as it wrote them — every tab copies the others'
      // entries through untouched, so a claim is never lost by somebody else's heartbeat.
      if (b.ask && typeof b.ask === 'object' && !Array.isArray(b.ask)) {
        const ask: Record<string, number> = {};
        for (const [k, t] of Object.entries<any>(b.ask)) if (k && Number.isFinite(t)) ask[k] = t;
        if (Object.keys(ask).length) one.ask = ask;
      }
      out[id] = one;
    }
  }
  return out;
}

function readSent(userId: string): Record<string, number> {
  let raw: string | null = null;
  try { raw = localStorage.getItem(sentKey(userId)); } catch { return {}; }
  try { const v = JSON.parse(raw || '{}'); return v && typeof v === 'object' && !Array.isArray(v) ? v : {}; } catch { return {}; }
}
function writeSent(userId: string, v: Record<string, number>): void {
  try { localStorage.setItem(sentKey(userId), JSON.stringify(v)); } catch { /* unshared — see readBeats */ }
}

/** When that tab last said it was asking something, on any page — 0 when it is asking nothing. */
function latestClaim(b: Beat): number {
  let t = 0;
  const ask = b.ask || {};
  for (const k of Object.keys(ask)) t = Math.max(t, Number(ask[k]) || 0);
  return t;
}

let lastSeen = Date.now();     // when this tab was last looked at
let beatTimer = 0;
let beaters = 0;
let beatFor = '';              // the login the heartbeat is running for
let beatPage: string | null = null;   // the page the last beat reported

function beat(userId: string): void {
  const now = Date.now();
  if (document.visibilityState === 'visible') lastSeen = now;
  const beats = readBeats(userId);
  if (!beats) return;
  const page = pageHere();
  beatPage = page;
  const self: Beat = { key: page, at: now, vis: lastSeen };
  // The pages this tab is asking on, re-stamped while it still is: a claim lives on the heartbeat behind
  // it, so a tab that is killed outright lets its questions go once that heartbeat has stopped.
  const ask: Record<string, number> = {};
  for (const k of Object.keys(myClaims)) { myClaims[k] = now; ask[k] = now; }
  if (Object.keys(ask).length) self.ask = ask;
  const next: Beats = { [myTab()]: self };
  for (const [id, b] of Object.entries(beats)) {
    if (id === myTab()) continue;
    // A tab is dropped once its heartbeat has stopped (GONE_MS) — unless it is still asking a question
    // somewhere, which keeps its place a while longer (CLAIM_MS). A browser throttles a background tab's
    // timers to about one a minute, and dropping its place between two of those beats is what let the
    // other tabs take its question and give it straight back. A tab that is CLOSED is not waited for:
    // it says so itself on the way out (leave, below).
    if (now - b.at < GONE_MS || now - latestClaim(b) < CLAIM_MS) next[id] = b;
  }
  try { localStorage.setItem(beatsKey(userId), JSON.stringify(next)); } catch { /* unshared — see readBeats */ }
  // This tab has the page up, so whatever was sent to it has arrived.
  if (page) {
    const sent = readSent(userId);
    if (page in sent) { delete sent[page]; writeSent(userId, sent); }
  }
}

/** A page change is said at once rather than at the next beat — once, however many pop-ups notice it. */
function beatMoved(userId: string): void {
  if (pageHere() === beatPage) return;
  beat(userId);
}

/** Leaves the shared record, so a tab being closed doesn't hold its page — or a question it was asking —
 *  for another GONE_MS. A tab says this on the way out, which is why nothing has to wait CLAIM_MS for a
 *  tab that was simply closed. */
function leave(userId: string): void {
  const beats = readBeats(userId);
  if (!beats || !(myTab() in beats)) return;
  delete beats[myTab()];
  try { localStorage.setItem(beatsKey(userId), JSON.stringify(beats)); } catch { /* unshared */ }
}

/** The tab in front of the others: one that was being looked at when it last spoke (`vis >= at`), and
 *  among those simply the first name in order. NOT the one looked at most recently: with two windows
 *  side by side both are visible and both re-stamp `vis` at every beat, so "most recently" handed the
 *  turn from one to the other every 4 seconds and the same question appeared, vanished and appeared
 *  again (report c2846304, opening #2). `live` comes in sorted. */
function frontTab(beats: Beats, live: string[]): string {
  for (const id of live) if (beats[id].vis >= beats[id].at) return id;
  return live[0] || '';
}

// ── Which tab is asking: the claim ───────────────────────────────────────────────────────────────────
// The answer used to be worked out afresh in every tab every LOOK_MS from where the windows were at that
// moment, so a question moved the instant the front tab changed — and it changed every beat. Opening #2
// of report c2846304: "seems to get stuck in a loop… the requests that are not related to that screen
// pop up, then disappear. Then the notification pops up, disappears, and the requests pop up again."
//
// The decision is now LATCHED. A tab that puts a question up writes a claim into its own beat (`ask`:
// page key → when it last said so, re-stamped by every heartbeat) and keeps that question while it still
// has something to ask and is still beating; every other tab stays quiet. It lets go when the question is
// answered or gone, when the tab is closed (`leave` on pagehide), or when the tab stops beating
// altogether for CLAIM_MS. Only two things move a question off a claiming tab: the page it belongs on
// coming up ON SCREEN in another tab (that is where it belongs, however it was opened), and the claiming
// tab being neither on that page nor in front of the user any more — otherwise a question could sit
// unseen in a window the user had left behind. A window coming to the front, a visibility change or a
// skipped heartbeat move nothing at all.
const claimers: Record<string, Set<number>> = {};   // page key → the pop-ups in THIS tab that want it
const myClaims: Record<string, number> = {};        // page key → when this tab claimed it

// One card per page however many pop-ups call that page home: the first to ask holds it until it has
// nothing left to ask or is unmounted. It used to be let go at any look whose answer was not 'notice'
// and taken over by the next pop-up at its own look, which is the other half of the blinking above.
const cardHeld: Record<string, number> = {};

/** This pop-up lets go of a page. @returns whether the shared record now has to be written again. */
function drop(key: string, askerId: number): boolean {
  if (cardHeld[key] === askerId) delete cardHeld[key];
  const set = claimers[key];
  if (!set || !set.delete(askerId) || set.size) return false;   // another pop-up here still asks on it
  delete claimers[key];
  if (!(key in myClaims)) return false;
  delete myClaims[key];
  return true;
}

/** This tab is the one asking on `key`: claim it, and let go of any page this pop-up claimed before. */
function hold(userId: string, key: string, askerId: number): void {
  let write = false;
  for (const k of Object.keys(claimers)) if (k !== key) write = drop(k, askerId) || write;
  const set = claimers[key] || (claimers[key] = new Set<number>());
  set.add(askerId);
  if (!(key in myClaims)) { myClaims[key] = Date.now(); write = true; }
  if (write && userId) beat(userId);
}

/** This pop-up has nothing left to ask, or has gone: every page it claimed is let go at once rather than
 *  at the next beat, so the tab that should ask next hears about it now. */
function letGo(userId: string, askerId: number): void {
  let write = false;
  for (const k of Object.keys(claimers)) write = drop(k, askerId) || write;
  if (write && userId) beat(userId);
}

/** Who asks this page's question — worked out the same way in every tab, from the shared record alone, so
 *  all of them arrive at the one answer. `live` comes in sorted, and so does every list here, so two tabs
 *  that put the question up in the same breath still settle on one of them. */
function askerFor(beats: Beats, live: string[], key: string, now: number): string {
  const all = Object.keys(beats).sort();
  const watched = (id: string) => beats[id].vis >= beats[id].at;   // looked at when it last spoke
  const onPage = (id: string) => beats[id].key === key;
  const claims = all.filter(id => { const t = Number(beats[id].ask?.[key]) || 0; return t > 0 && now - t < CLAIM_MS; });
  // 1. The tab that has the page open AND the question already in it: a half-written answer never moves
  //    tabs, and a tab the browser has throttled keeps its turn instead of losing it between heartbeats.
  const asking = claims.filter(onPage);
  if (asking.length) return asking[0];
  // 2. The page is open in a tab the user is looking at — that is where the question belongs, so it moves
  //    there the moment the page comes up, however it was opened.
  const here = live.filter(id => onPage(id) && watched(id));
  if (here.length) return here[0];
  // 3. A tab already asking somewhere else, while it is still the one in front of the user.
  const still = claims.filter(watched);
  if (still.length) return still[0];
  // 4. The page is open in a tab the user is not looking at; 5. nobody has it: the tab in front.
  const open = live.filter(onPage);
  return open[0] || frontTab(beats, live);
}

// The tab this one last worked out as the asker, per page, and when. A tab that has merely skipped a
// heartbeat has not let go — a browser throttles a background tab's timers to about one a minute — so its
// turn stands for one beat longer before the question is moved anywhere. Without that grace the question
// was handed to the tab in front and handed straight back at the next beat.
const heldBy: Record<string, { id: string; at: number }> = {};

// Pages this tab was told to open and that never came up — the third fallback. Kept here rather than in
// the shared record so it lasts exactly as long as this page does: a reload asks with a card again. Once
// set it STAYS set: it used to be forgotten the moment any tab reported the page, which a background tab
// does once a minute, and the question then fell back to the card and came round to the dialog again.
const gaveUp = new Set<string>();

function weigh(userId: string, me: Me, key: string, page: string): PopupHome {
  const beats = readBeats(userId);
  if (!beats) return 'show';                                    // fallback 1: nothing is shared here
  const now = Date.now();
  const mine = myTab();
  const live = Object.keys(beats).filter(id => now - beats[id].at < GONE_MS).sort();
  let asker = askerFor(beats, live, key, now) || mine;
  const was = heldBy[key];
  if (was && was.id !== asker && live.indexOf(was.id) < 0 && now - was.at < GRACE_MS) asker = was.id;
  else heldBy[key] = { id: asker, at: now };
  if (asker !== mine) return 'hidden';                          // another tab is asking it
  // This tab is the one. The dialog where the page itself is open, or where this login cannot open that
  // page at all (fallback 2, services/homePage.ts: My Tasks and the payment-slip pop-up both reach people
  // who cannot open the bank pages); the card where the page is open in no tab.
  if (page === key) return 'show';
  if (!mayOpen(key, me)) return 'show';
  if (gaveUp.has(key)) return 'show';
  const sent = Number(readSent(userId)[key]) || 0;
  if (!sent) return 'notice';                                   // offer the card
  if (now - sent < SENT_MS) return 'hidden';                    // the page is on its way
  gaveUp.add(key);                                              // fallback 3: it never came up
  const left = readSent(userId);
  if (key in left) { delete left[key]; writeSent(userId, left); }
  return 'show';
}

/** The card's click: the page opens in the tab that already answers to it, or in a new one, and this
 *  round is marked as sent. Called from a real click, so the browser allows the window. */
export function openPopupHome(userId: string, key: string): void {
  if (userId) {
    const sent = readSent(userId);
    sent[key] = Date.now();
    writeSent(userId, sent);
  }
  window.open(pageHref(key), `slf-page-${key}`);
}

let askerSeq = 0;    // names the pop-ups in this tab apart (cardHeld / claimers above)

/** Where a card sits in the corner's stack: with a question waiting on two different pages, the second
 *  card lines up under the first instead of on top of it. */
export const popupHomeSlot = (key: string): number => {
  const i = Object.keys(cardHeld).sort().indexOf(key);
  return i < 0 ? 0 : i;
};

export function usePopupHome(key: string, pending: boolean): PopupHome {
  const userId = useStore(s => s.me?.userId) || '';
  const loc = useLocation();
  const [, bump] = useState(0);
  const idRef = useRef(0);
  if (!idRef.current) idRef.current = ++askerSeq;
  const askerId = idRef.current;
  // A Dashboard tab change is not an address change, so it is heard rather than re-read. The one look on
  // subscribing catches a Dashboard that said which page it has between this render and this effect — it
  // draws before these pop-ups do, so on a sign-in its word would otherwise be missed.
  useEffect(() => {
    const f = () => bump(n => n + 1);
    dashSubs.add(f);
    f();
    return () => { dashSubs.delete(f); };
  }, []);
  const page = pageOfPath(loc.pathname);

  // The window name that lets another tab's card open the page HERE rather than in a tab of its own:
  // window.open(pageHref(key), 'slf-page-<key>') finds it by name. Never over a name this window already
  // answers to — PandaAI Email's EMAIL_TAB above all (NewMailToast, report b6182c13), which is how a
  // new-mail card finds the Email tab.
  useEffect(() => {
    // Say where this tab is NOW rather than at the next beat, so two tabs opening the same page a second
    // apart never both read themselves as the one showing it.
    if (userId) beatMoved(userId);
    if (!page || !HOME_PAGES.has(page)) return;
    const name = `slf-page-${page}`;
    if (window.name && window.name.indexOf('slf-page-') !== 0) return;
    window.name = name;
    return () => { if (window.name === name) window.name = ''; };
  }, [page, userId]);

  // The heartbeat is the tab's, not this pop-up's: one timer however many pop-ups are mounted. A tab
  // somebody else signs into starts a new one, under the new login's own record.
  useEffect(() => {
    if (!userId) return;
    beaters++;
    if (beatTimer && beatFor !== userId) { window.clearInterval(beatTimer); beatTimer = 0; }
    if (!beatTimer) {
      beatFor = userId;
      beat(userId);
      beatTimer = window.setInterval(() => beat(userId), BEAT_MS);
    }
    // Said both ways round: a tab the user has just left says so at once (its beat stamps `at` past
    // `vis`), instead of reading as the tab in front for up to another 4 seconds.
    const seen = () => beat(userId);
    const gone = () => leave(userId);
    document.addEventListener('visibilitychange', seen);
    window.addEventListener('pagehide', gone);
    return () => {
      document.removeEventListener('visibilitychange', seen);
      window.removeEventListener('pagehide', gone);
      beaters--;
      if (beaters <= 0 && beatTimer) { window.clearInterval(beatTimer); beatTimer = 0; beatFor = ''; }
    };
  }, [userId]);

  const [state, setState] = useState<PopupHome>('hidden');
  useEffect(() => {
    const look = () => {
      const who = useStore.getState().me;
      let next: PopupHome = 'hidden';
      // A pop-up with no page of its own to go to — My Tasks' alert can carry a row the server named
      // nothing for — is asked where the user is, as it always was.
      if (pending) next = !key ? 'show' : who?.userId ? weigh(who.userId, who, key, pageHere()) : 'hidden';
      // While this tab is the one asking on the page it says so in the shared record, so no other tab
      // takes the question over at its next look; it lets go the moment it has nothing to ask there.
      if (pending && !!key && (next === 'show' || next === 'notice')) hold(who?.userId || '', key, askerId);
      else letGo(who?.userId || '', askerId);
      if (next === 'notice') {
        const held = cardHeld[key];
        if (held && held !== askerId) next = 'hidden';            // another pop-up here already has the card
        else cardHeld[key] = askerId;
      }
      setState(next);
    };
    look();
    const timer = window.setInterval(look, LOOK_MS);
    // Another tab taking the page, or being sent to it, is heard at once rather than at the next look.
    const shared = (e: StorageEvent) => { if (!e.key || e.key.indexOf('mf_popup') === 0) look(); };
    window.addEventListener('storage', shared);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('storage', shared);
    };
  }, [key, pending, page, userId, askerId]);

  // The claim and the card go when this pop-up goes — not at every change of what it is asking about,
  // which is what let them change hands between pop-ups while the question itself stayed up.
  useEffect(() => () => letGo(useStore.getState().me?.userId || '', askerId), [askerId]);

  return state;
}
